import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Download } from "lucide-react";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

const VIEW_KEY = "wh_pv_count";
const DISMISS_KEY = "wh_install_dismissed";
const MIN_VIEWS = 2;

export function InstallPrompt() {
  const location = useLocation();
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);

  // Track page views
  useEffect(() => {
    try {
      const count = Number(sessionStorage.getItem(VIEW_KEY) || "0") + 1;
      sessionStorage.setItem(VIEW_KEY, String(count));
    } catch {
      // ignore
    }
  }, [location.pathname]);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      try {
        if (localStorage.getItem(DISMISS_KEY) === "1") return;
        const count = Number(sessionStorage.getItem(VIEW_KEY) || "0");
        if (count >= MIN_VIEWS) setVisible(true);
        else {
          const t = setInterval(() => {
            const c = Number(sessionStorage.getItem(VIEW_KEY) || "0");
            if (c >= MIN_VIEWS) {
              setVisible(true);
              clearInterval(t);
            }
          }, 1500);
          setTimeout(() => clearInterval(t), 60_000);
        }
      } catch {
        setVisible(true);
      }
    };
    window.addEventListener("beforeinstallprompt", handler as EventListener);
    return () => window.removeEventListener("beforeinstallprompt", handler as EventListener);
  }, []);

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // ignore
    }
  };

  const install = async () => {
    if (!deferred) return;
    try {
      await deferred.prompt();
      await deferred.userChoice;
    } finally {
      setDeferred(null);
      setVisible(false);
    }
  };

  if (!visible || !deferred) return null;

  return (
    <div
      role="dialog"
      aria-label="Install we-help.in app"
      className="fixed bottom-4 left-4 right-4 z-50 mx-auto max-w-md rounded-xl border border-border bg-card p-4 shadow-card sm:left-auto sm:right-4"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <Download className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-foreground">
            Add we-help.in to your home screen
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Faster access to free career programs from your home screen.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              onClick={install}
              className="min-h-[44px] rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground"
            >
              Install
            </button>
            <button
              onClick={dismiss}
              className="min-h-[44px] rounded-lg px-3 text-sm text-muted-foreground hover:bg-muted"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          className="ml-1 -mt-1 rounded p-1 text-muted-foreground hover:bg-muted"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export default InstallPrompt;